/**
 * The agent's envelope, reduced to one shape the app can act on.
 *
 * The worker publishes `{ reply, data, actions }` on `agent_event`, but the
 * prompts behind it spell the same instruction three ways — `data.redux_action`
 * as a bare name or as `{ type, payload }`, `data.redux_actions` as a list of
 * names whose fields sit flat beside the list, and `data.redux_variables` as a
 * list of `{ redux_action, value }` entries. The worker itself accepts all
 * three (`livekit_worker.py:3279-3289`), so the client has to as well: a
 * command dropped here because of how the model happened to phrase it is a
 * turn where the agent said it did something and nothing happened.
 *
 * Navigation has two forms, both Ki_UI's: `actions.navigate` (+ `params`), and
 * `data.navigation: { screen, params }`, which is the one today's backend lets
 * through for the web.
 *
 * Nothing here decides what a command DOES. This only normalises; the executor
 * applies the PMS allowlist, so an unknown type survives normalisation and is
 * refused (and logged) there, in one place.
 *
 * Pure: no React, no DOM, no store — importable from Node check scripts.
 */

/**
 * Keys that describe the envelope rather than a command's arguments.
 *
 * A bare `redux_action: "TYPE"` takes the rest of `data` as its payload, so
 * these must be kept out of it. Routing fields matter most: echoing
 * `agent_action` / `target_agent` into a payload that later rides back in
 * context would be re-read by the worker as a fresh handoff instruction, and
 * `navigation` is the envelope's own screen change, not a form field.
 */
export const RESERVED_KEYS = [
  'redux_action',
  'redux_actions',
  'redux_variables',
  'agent_action',
  'target_agent',
  'agent_initial_prompt',
  'call_back_navigation',
  'reply',
  'message',
  'msg',
  'language',
  'navigation',
];

/**
 * Commands the retail web prompts still emit that have no PMS surface.
 *
 * The executor answers each with one `[api_result] … failed` so the agent
 * stops waiting for a result that is never coming, instead of leaving it to
 * tell the user an order or goal went through.
 */
export const RETAIL_SIGNALS = [
  'REQUEST_TRADE_SUBMIT',
  'REQUEST_GOAL_SUBMIT',
  'REQUEST_THESIS_SUBMIT',
  'REQUEST_DEMAT_SUBMIT',
  'REQUEST_PRESET_BUY',
  'REQUEST_GOLD_DELIVERY_SUBMIT',
  'REQUEST_LOGIN_SUBMIT',
];

/**
 * The twelve web agents that share the unified institutional room, with the
 * name the chat header shows for each.
 *
 * `login_web` is deliberately absent: it is not part of the unified room, it
 * is unauthenticated, and a handoff can never land on it.
 */
export const AGENT_LABELS = {
  trading_web: 'Trading',
  portfolio_analytics_web: 'Portfolio Analytics',
  market_knowledge_web: 'Market Knowledge',
  screener_web: 'Screener',
  investment_portfolio_recommendation_web: 'Recommendations',
  trade_execution_web: 'Trade Execution',
  goal_planning_web: 'Goal Planning',
  gold_buy_sell_web: 'Gold',
  customer_service_web: 'Support',
  policy_web: 'Policy',
  ekyc_web: 'eKYC',
  advisor_web: 'Advisor',
  /* The interactive research analyst: minted per stock, in its own web room. */
  stock_research_web: 'Stock Research',
  /* The interactive MF research analyst: minted per fund, in its own web room. */
  mf_research_web: 'Fund Research',
};

/**
 * Prompt spellings that predate the backend's agent-type enum. Mirrors
 * `_ALIAS_TO_CANONICAL` in `bolodhan_unified/factory_registry.py`, which is
 * what the worker applies before it switches.
 */
const AGENT_ALIASES = {
  trade: 'trading',
  kyc: 'ekyc',
  /* The backend's own name for the MF analyst's web agent. */
  mf_interactive_research_analyst: 'mf_research',
};

const CONNECT_AGENT = 'CONNECT_AGENT';

const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v)
  && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null);

const text = (v) => (typeof v === 'string' ? v.trim() : '');

/**
 * A deep copy of JSON-shaped data.
 *
 * Payloads are handed to the executor and on to page handlers, which are free
 * to reshape what they receive. Copying here means none of that can reach back
 * into the envelope the provider keeps for deduplication.
 */
function cloneData(value, seen = new WeakSet()) {
  if (Array.isArray(value)) {
    if (seen.has(value)) return undefined;
    seen.add(value);
    const out = value.map((v) => cloneData(v, seen)).filter((v) => v !== undefined);
    seen.delete(value);
    return out;
  }
  if (isPlainObject(value)) {
    if (seen.has(value)) return undefined;
    seen.add(value);
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const c = cloneData(v, seen);
      if (c !== undefined) out[k] = c;
    }
    seen.delete(value);
    return out;
  }
  if (typeof value === 'function' || typeof value === 'symbol' || value === undefined) return undefined;
  if (typeof value === 'bigint') return String(value);
  return value;
}

/**
 * A command type as the allowlist spells it.
 *
 * Upper-cased because the model occasionally lower-cases a constant it copied
 * from the prompt; the vocabulary is all upper snake case, so nothing valid is
 * lost and `request_run_analysis` still runs.
 */
const commandType = (v) => {
  const t = text(v);
  return t ? t.toUpperCase() : '';
};

/** Whatever a single value counts as "nothing" — see `isEmptyPayload`. */
function isEmptyValue(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.every(isEmptyValue);
  if (isPlainObject(v)) return Object.values(v).every(isEmptyValue);
  return false;
}

/**
 * True when a payload carries nothing to act on.
 *
 * `UPDATE_*` / `SET_*` commands with an empty payload are the model emitting a
 * dispatch it had no data for; applying one would clear state the user can
 * see (prompt rule 9 in the backend loader says the same). Zero and `false`
 * are values, not emptiness — `{ dummy: false }` is a real payload.
 */
export function isEmptyPayload(payload) {
  return isEmptyValue(payload);
}

/**
 * JSON with object keys sorted at every depth.
 *
 * Two packets that differ only in key order are the same instruction, and the
 * provider drops a repeat inside a short window — so the key must not depend
 * on the order the worker's `json.dumps` happened to write.
 */
export function envelopeKey(payload) {
  const seen = new WeakSet();
  const walk = (v) => {
    if (v === undefined || typeof v === 'function' || typeof v === 'symbol') return undefined;
    if (typeof v === 'bigint') return String(v);
    if (v === null || typeof v !== 'object') return v;
    if (seen.has(v)) return '[circular]';
    seen.add(v);
    let out;
    if (Array.isArray(v)) {
      out = v.map((x) => {
        const w = walk(x);
        return w === undefined ? null : w;
      });
    } else {
      out = {};
      for (const k of Object.keys(v).sort()) {
        const w = walk(v[k]);
        if (w !== undefined) out[k] = w;
      }
    }
    seen.delete(v);
    return out;
  };
  try {
    const json = JSON.stringify(walk(payload));
    return json === undefined ? '' : json;
  } catch (e) {
    return '';
  }
}

/**
 * The web agent type a handoff target names, or null when there is none.
 *
 * Prompts name the target loosely — `market_knowledge`, `trade`,
 * `trading_web`, occasionally the internal `…_web_agent` name — and the worker
 * maps every one of them onto the web sibling before switching. The client has
 * to land on the same agent, because the next reconnect mints a token for it,
 * and a token for the wrong agent switches the live session back.
 *
 * Unknown roles return null rather than an invented `x_web`: the caller then
 * keeps the agent it has, which is also what the worker does when it refuses
 * a target.
 */
export function normalizeAgentType(target) {
  let t = text(target).toLowerCase().replace(/[\s-]+/g, '_');
  if (!t) return null;
  if (t.endsWith('_agent')) t = t.slice(0, -'_agent'.length);
  if (t.endsWith('_web')) t = t.slice(0, -'_web'.length);
  t = AGENT_ALIASES[t] || t;
  const type = `${t}_web`;
  return Object.prototype.hasOwnProperty.call(AGENT_LABELS, type) ? type : null;
}

/** `data` minus the envelope's own keys: the payload a bare command name takes. */
function flatPayload(data, extraReserved) {
  const out = {};
  for (const [k, v] of Object.entries(data)) {
    if (RESERVED_KEYS.includes(k) || extraReserved.includes(k)) continue;
    const c = cloneData(v);
    if (c !== undefined) out[k] = c;
  }
  return out;
}

/**
 * Commands carry an object payload. A scalar or a list the model put in the
 * payload slot is kept under `value` rather than dropped, so a handler can
 * still read `SET_AUTO_SELECT_GOAL_ID` sent as `{ payload: 42 }`.
 */
const asPayload = (v) => {
  const c = cloneData(v);
  if (c === undefined || c === null) return undefined;
  return isPlainObject(c) ? c : { value: c };
};

const NAME_KEYS = ['type', 'redux_action', 'action'];

/**
 * One `{ type, payload }` from an object entry.
 *
 * `{ type, payload }` is the prompts' documented form. An object with no
 * `payload` but other fields (`{ type: 'UPDATE_KYC_DATA', email: … }`) takes those
 * fields; a bare `{ type }` falls back to the flat `data`, like a name string.
 */
function fromObject(entry, flat) {
  const type = commandType(NAME_KEYS.map((k) => entry[k]).find((v) => typeof v === 'string' && v.trim()));
  if (!type) return null;
  if (Object.prototype.hasOwnProperty.call(entry, 'payload')) {
    return { type, payload: asPayload(entry.payload) ?? {} };
  }
  const rest = {};
  for (const [k, v] of Object.entries(entry)) {
    if (NAME_KEYS.includes(k)) continue;
    const c = cloneData(v);
    if (c !== undefined) rest[k] = c;
  }
  return { type, payload: Object.keys(rest).length ? rest : cloneData(flat) };
}

/**
 * One `{ type, payload }` from a `redux_variables` entry.
 *
 * The entry is `{ redux_action, value?, variable?, …rest }`. An object `value`
 * is the payload; `variable` + `value` sets that one field (Ki_UI's H4 form);
 * otherwise the payload is the entry's remaining fields, with a scalar `value`
 * kept among them so `{ redux_action: 'X', value: 'hi' }` does not arrive empty.
 */
function fromVariable(entry, flat) {
  if (typeof entry === 'string') {
    const type = commandType(entry);
    return type ? { type, payload: cloneData(flat) } : null;
  }
  if (!isPlainObject(entry)) return null;
  const type = commandType(NAME_KEYS.map((k) => entry[k]).find((v) => typeof v === 'string' && v.trim()));
  if (!type) return null;
  const { value, variable } = entry;
  if (isPlainObject(value)) return { type, payload: cloneData(value) };
  if (typeof variable === 'string' && variable.trim() && value !== undefined) {
    const c = cloneData(value);
    return { type, payload: c === undefined ? {} : { [variable.trim()]: c } };
  }
  const rest = {};
  for (const [k, v] of Object.entries(entry)) {
    if (NAME_KEYS.includes(k) || k === 'variable') continue;
    const c = cloneData(v);
    if (c !== undefined) rest[k] = c;
  }
  return { type, payload: rest };
}

/** The command list from the three spellings, in the order they appear. */
function collectCommands(data, flat) {
  const out = [];
  const push = (cmd) => { if (cmd) out.push(cmd); };

  const single = data.redux_action;
  if (typeof single === 'string') {
    const type = commandType(single);
    if (type) push({ type, payload: cloneData(flat) });
  } else if (isPlainObject(single)) {
    push(fromObject(single, flat));
  }

  /* A single name where a list belongs is still one instruction. */
  const many = typeof data.redux_actions === 'string' ? [data.redux_actions] : data.redux_actions;
  if (Array.isArray(many)) {
    for (const entry of many) {
      if (typeof entry === 'string') {
        const type = commandType(entry);
        if (type) push({ type, payload: cloneData(flat) });
      } else if (isPlainObject(entry)) {
        push(fromObject(entry, flat));
      }
    }
  }

  const vars = isPlainObject(data.redux_variables) ? [data.redux_variables] : data.redux_variables;
  if (Array.isArray(vars)) for (const entry of vars) push(fromVariable(entry, flat));

  /* The prompts often repeat one instruction in two spellings — a
     `redux_action` name AND the same name inside `redux_actions`. Ki_UI
     skipped the single form when the list was present for that reason. Run
     twice, a run request would start two jobs and send two reports, and each
     report costs a spoken LLM turn; so an exact repeat is dropped. */
  const seen = new Set();
  return out.filter((cmd) => {
    const key = `${cmd.type}\u0000${envelopeKey(cmd.payload)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Screen name from `actions.navigate`, or the leaf of `actions.path`. */
function actionsScreen(actions) {
  let name = text(actions.navigate);
  if (!name && Array.isArray(actions.path)) {
    const leaf = [...actions.path].reverse().find((p) => typeof p === 'string' && p.trim());
    name = text(leaf);
  } else if (!name && typeof actions.path === 'string') {
    name = text(actions.path);
  }
  /* Ki_UI accepts `"Stack/Screen"`; only the leaf names a screen. */
  if (name.includes('/')) name = text(name.split('/').filter((s) => s.trim()).pop());
  return name;
}

/**
 * `data.navigation` as `{ screen, params }`.
 *
 * Ki_UI's form is `{ screen, stack?, params? }`; a bare screen name is taken as
 * well, and `navigate` / `name` stand in for `screen`. A navigation with no
 * screen is kept with `screen: ''` so the executor reports the failure instead
 * of the agent believing it navigated.
 */
function dataNavigate(value) {
  if (typeof value === 'string') return { screen: text(value), params: {}, source: 'data' };
  const nav = isPlainObject(value) ? value : {};
  let screen = text(nav.screen) || text(nav.navigate) || text(nav.name);
  /* Ki_UI accepts `"Stack/Screen"`; only the leaf names a screen. */
  if (screen.includes('/')) screen = text(screen.split('/').filter((p) => p.trim()).pop());
  return { screen, params: isPlainObject(nav.params) ? cloneData(nav.params) : {}, source: 'data' };
}

/**
 * Everything one envelope asks of the app, normalised.
 *
 * - `reply`: the text to show — `reply`, else top-level `message` / `text`,
 *   else `data.message` / `data.msg` (Ki_UI rendered those as the agent's line).
 * - `commands`: every command from the three spellings, in order.
 * - `navigate`: `data.navigation`, else `actions.navigate`. The data form wins
 *   because it is what today's backend lets through for the web. The
 *   `actions` form is otherwise honoured (the executor ignores a name that is
 *   not a PMS screen) EXCEPT on the handoff pre-publish — a blank reply with
 *   `CONNECT_AGENT` — whose `actions` skip the worker's web filter
 *   (`livekit_worker.py:2134-2149`) and carry the outgoing agent's React Native
 *   navigation. Resolving loosely is not enough to discard those: the app's
 *   `Profile` screen shares its name with the phone app's, so a leaked
 *   `Profile` would move the user. The outgoing agent is never the one meant
 *   to navigate; the receiving agent's own envelope follows. `data.navigation`
 *   is kept even there: the worker builds that packet's `data` itself, so a
 *   navigation in it was written for this app.
 * - `handoff`: `data.agent_action === 'CONNECT_AGENT'` with a target.
 * - `empty`: none of the above — nothing to render, nothing to run.
 *
 * Tolerates a JSON string, and an envelope whose commands sit at the top level
 * instead of under `data`.
 */
/**
 * The answer's DATA the screens can draw, beside what the reply says:
 * `suggestedStocks` (a screened or ranked list the agent built) and
 * `stock_trades` (the trade generator's answer, sized for an account), with
 * the handle a run was published under. Kept so the screen the agent opens
 * shows the same names and trades the reply quotes, instead of only the
 * backend's own list. Null when the envelope carries neither.
 */
function dataPayload(data) {
  if (!isPlainObject(data)) return null;
  const stocks = Array.isArray(data.suggestedStocks) ? data.suggestedStocks
    : Array.isArray(data.suggested_stocks) ? data.suggested_stocks : null;
  const rawTrades = data.stock_trades ?? data.stockTrades ?? data.trade_ideas ?? data.tradeIdeas ?? null;
  const trades = Array.isArray(rawTrades) ? { trades: rawTrades }
    : isPlainObject(rawTrades) && Array.isArray(rawTrades.trades) ? rawTrades : null;
  const urlId = text(data.analysis_url_id) || text(data.analysisUrlId) || text(data.url_id) || '';
  if (!stocks?.length && !trades?.trades?.length) return null;
  const count = Number(data.count);
  return {
    suggestedStocks: stocks ? cloneData(stocks.filter(isPlainObject).slice(0, 200)) : null,
    stockTrades: trades ? cloneData({ ...trades, trades: trades.trades.filter(isPlainObject).slice(0, 50) }) : null,
    analysisUrlId: urlId || null,
    count: Number.isFinite(count) ? count : null,
    screenType: text(data.screenType) || text(data.screen_type) || null,
    source: text(data.list_source) || text(data.source) || null,
  };
}

export function normalizeEnvelope(payload) {
  let env = payload;
  if (typeof env === 'string') {
    try {
      env = JSON.parse(env);
    } catch (e) {
      env = null;
    }
  }
  if (!isPlainObject(env)) {
    return { reply: '', commands: [], navigate: null, handoff: null, payload: null, empty: true };
  }

  const topLevelCommands = ['redux_action', 'redux_actions', 'redux_variables', 'agent_action']
    .some((k) => env[k] !== undefined);
  const usesTopLevel = !isPlainObject(env.data) && topLevelCommands;
  const data = isPlainObject(env.data) ? env.data : usesTopLevel ? env : {};
  /* When `data` IS the envelope, its own framing keys are not arguments. */
  const flat = flatPayload(data, usesTopLevel ? ['data', 'actions', 'text'] : []);

  const reply = text(env.reply) || text(env.message) || text(env.text)
    || text(data.message) || text(data.msg);

  const commands = collectCommands(data, flat);
  let navigate = data.navigation !== undefined && data.navigation !== null
    ? dataNavigate(data.navigation)
    : null;

  let handoff = null;
  if (commandType(data.agent_action) === CONNECT_AGENT) {
    const target = text(data.target_agent);
    /* The stock or fund a per-subject agent (stock_research_web, mf_research_web) is to be opened on. */
    const params = isPlainObject(data.agent_params) ? data.agent_params
      : isPlainObject(data.agent_initial_params) ? data.agent_initial_params : {};
    const subject = text(data.security_symbol) || text(data.securitySymbol) || text(data.symbol)
      || text(params.security_symbol) || text(params.securitySymbol) || text(params.symbol);
    if (target) handoff = { target, prompt: text(data.agent_initial_prompt), ...(subject ? { subject } : null) };
  }

  /* The interactive analysts' events — a new revision, an approval card, an
     assumption conflict — for the session screen on show. */
  const analystEvents = Array.isArray(data.analyst_events)
    ? cloneData(data.analyst_events.filter(isPlainObject).slice(0, 50))
    : [];
  const analystSessionId = Number(data.session_id ?? data.sessionId) || null;

  const prePublish = Boolean(handoff) && !reply;
  if (!navigate && !prePublish && isPlainObject(env.actions)) {
    const screen = actionsScreen(env.actions);
    if (screen) {
      navigate = {
        screen,
        params: isPlainObject(env.actions.params) ? cloneData(env.actions.params) : {},
        source: 'actions',
      };
    }
  }

  const dataPayloadOut = dataPayload(data);

  return {
    reply,
    commands,
    navigate,
    handoff,
    payload: dataPayloadOut,
    analystEvents,
    analystSessionId,
    empty: !reply && !commands.length && !navigate && !handoff && !dataPayloadOut && !analystEvents.length,
  };
}
